import { Button, Modal } from "antd";
import axios, { AxiosResponse } from "axios";
import { message } from "@bindeleddet/core/utils";
import { EventRegistrationStatus, IsOpenType } from "@bindeleddet/core/types";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import { useCallback, useEffect, useState } from "react";
import { getCurrentTimeNorwegian, parseDateAsNorwegianTime } from "helpers/dateUtils";
import { LABELS_PER_STATUS } from "./constants";

interface Props {
  eventId: number;
  open: string;
  isOpen: IsOpenType;
  externalBookingUrl: string | null;
}

const openInNewTab = (url: string) => {
  window.open(url, "_blank")?.focus();
};

const RegisterButton: React.FC<Props> = ({ eventId, open, isOpen, externalBookingUrl }) => {
  const [eventStatus, setEventStatus] = useState<EventRegistrationStatus>();
  const [countDown, setCountDown] = useState("");
  const [loading, setLoading] = useState(true);
  const { currentUser } = useLoginContext();

  const fetchEventStatus = useCallback(() => {
    if (currentUser?.id && !isCompanyUser(currentUser)) {
      setLoading(true);
      axios
        .get(`/events/${eventId}/registration_status/`)
        .then(response => setEventStatus(response.data))
        .catch(() => message("error", "En feil har oppstått", "Kunne ikke hente arrangementets status"))
        .finally(() => setLoading(false));
    }
  }, [currentUser, eventId]);

  useEffect(() => {
    fetchEventStatus();
  }, [currentUser?.id, eventId, fetchEventStatus]);

  const registerForEvent = () => {
    const continueRegister = () => {
      setLoading(true);
      return axios
        .post<{ student: number; event: number }, AxiosResponse<{ registered: boolean; waitlisted: boolean }>>(
          `/events/${eventId}/register/`,
          {
            student: currentUser?.id,
            event: eventId,
          }
        )
        .then(response => {
          if (response.data.registered) {
            message("success", "Vellykket!", "Du er påmeldt arrangementet");
            return;
          }
          if (response.data.waitlisted) {
            message("success", "Vellykket!", "Du er nå på venteliste for arrangementet");
            return;
          }
          message(
            "error",
            "En feil har oppstått",
            "Det er usikkert om du ble meldt på arrangementet. Vennligst last inn siden på nytt."
          );
        })
        .catch(() => {
          if (eventStatus === "WAITLIST_AVAILABLE") {
            message("error", "En feil har oppstått", "Kunne ikke melde deg på ventelisten. Prøv igjen senere");
          } else message("error", "En feil har oppstått", "Kunne ikke melde deg på arrangementet. Prøv igjen senere");
        })
        .finally(() => fetchEventStatus());
    };

    // Send confirmation modal if deregistration is closed and the user is registering for event (not waitlist)
    if (isOpen === IsOpenType.DEREGISTERING_CLOSED && eventStatus === "AVAILABLE") {
      Modal.confirm({
        title: "Avmelding er stengt for dette arrangementet",
        content:
          "Om du melder deg på vil du ikke kunne melde deg av arrangementet. Er du sikker på at du vil fortsette?",
        okText: "Meld meg på",
        okType: "primary",
        cancelText: "Avbryt",
        onOk() {
          continueRegister();
        },
      });
    } else {
      continueRegister();
    }
  };

  const unregisterForEvent = () => {
    const continueUnregister = () => {
      setLoading(true);
      axios
        .post(`/events/${eventId}/unregister/`, {
          student: currentUser?.id,
          event: eventId,
        })
        .then(() => {
          if (eventStatus === "REGISTERED_FOR_WAITLIST") {
            message("success", "Vellykket!", "Du er nå meldt av ventelisten");
          } else {
            message("success", "Vellykket!", "Du er nå meldt av arrangementet");
          }
        })
        .catch(() => {
          if (eventStatus === "REGISTERED_FOR_WAITLIST") {
            message("error", "En feil har oppstått", "Kunne ikke melde deg av ventelisten. Prøv igjen senere");
          } else {
            message("error", "En feil har oppstått", "Kunne ikke melde deg av arrangementet. Prøv igjen senere");
          }
        })
        .finally(() => fetchEventStatus());
    };

    // Send confirmation modal if deregistration is closed and the user is registered (not waitlisted)
    if (isOpen === IsOpenType.DEREGISTERING_CLOSED && eventStatus === "REGISTERED") {
      Modal.confirm({
        title: "Avmelding er stengt for dette arrangementet",
        content: "Ved å melde deg av vil du motta 1 prikk. Er du sikker på at du vil fortsette?",
        okText: "Meld meg av",
        okType: "danger",
        cancelText: "Avbryt",
        onOk() {
          continueUnregister();
        },
      });
    } else {
      continueUnregister();
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const getTimeUntil = () => {
      const now = getCurrentTimeNorwegian();
      const openDate = parseDateAsNorwegianTime(open);
      let difference = (openDate.valueOf() - now.valueOf()) / 1000;

      if (difference <= 0) {
        clearInterval(intervalId);
        if (eventStatus === "NOT_OPEN") {
          fetchEventStatus();
        }
        return "0 sek";
      }

      const days = Math.floor(difference / 86400);
      difference -= days * 86400;

      const hours = Math.floor(difference / 3600) % 24;
      difference -= hours * 3600;

      const minutes = Math.floor(difference / 60) % 60;
      difference -= minutes * 60;

      const seconds = Math.floor(difference) % 60;

      if (days > 0) {
        if (days === 1 && hours === 1) return `${days} dag og ${hours} time`;
        if (days === 1) return `${days} dag og ${hours} timer`;
        if (hours === 1) return `${days} dager og ${hours} time`;
        return `${days} dager og ${hours} timer`;
      }
      if (hours > 0) {
        if (hours === 1) return `${hours} time og ${minutes} min`;
        return `${hours} timer og ${minutes} min`;
      }
      if (minutes > 0) return `${minutes} min og ${seconds} sek `;
      return `${seconds} sek `;
    };

    setCountDown(getTimeUntil());
    intervalId = setInterval(() => {
      setCountDown(getTimeUntil());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [open, fetchEventStatus, eventStatus]);

  if (isCompanyUser(currentUser)) return null;
  if (loading || !eventStatus)
    return (
      <Button size="large" style={{ color: "var(--font-color)", minWidth: "150px" }} disabled loading>
        Laster...
      </Button>
    );
  if (eventStatus === "EXTERNAL_BOOKING")
    return (
      <Button
        type="primary"
        size="large"
        onClick={() => externalBookingUrl && openInNewTab(externalBookingUrl)}
        disabled={!externalBookingUrl}
        style={{ minWidth: "150px" }}
      >
        {LABELS_PER_STATUS[eventStatus]}
      </Button>
    );
  if (eventStatus === "AVAILABLE" || eventStatus === "WAITLIST_AVAILABLE")
    return (
      <Button
        type="primary"
        size="large"
        onClick={registerForEvent}
        loading={loading}
        disabled={loading}
        style={{ minWidth: "150px" }}
      >
        {!loading ? LABELS_PER_STATUS[eventStatus] : "Melder på..."}
      </Button>
    );
  if (eventStatus === "REGISTERED" || eventStatus === "REGISTERED_FOR_WAITLIST")
    return (
      <Button
        size="large"
        type="primary"
        danger
        onClick={unregisterForEvent}
        loading={loading}
        disabled={loading || isOpen === IsOpenType.CLOSED}
        title={isOpen === IsOpenType.CLOSED ? "Arrangementet er stengt og du kan ikke melde deg av" : undefined}
        style={{ minWidth: "150px" }}
      >
        {!loading ? LABELS_PER_STATUS[eventStatus] : "Melder av..."}
      </Button>
    );
  return (
    <Button size="large" style={{ color: "var(--font-color)", minWidth: "150px" }} disabled loading={loading}>
      {isOpen === IsOpenType.CLOSED
        ? "Stengt"
        : eventStatus === "NOT_OPEN"
        ? `Åpner om ${countDown}`
        : LABELS_PER_STATUS[eventStatus]}
    </Button>
  );
};

export default RegisterButton;
